import { useState, useEffect, useCallback } from "react";

const CACHE_KEY = "ejobs_user_location";
const CACHE_TTL = 15 * 60 * 1000; // 15 minutes

export function useUserLocation() {
  const [location, setLocation] = useState(null);
  const [status, setStatus] = useState("idle"); // idle | locating | granted | denied | unsupported | error
  const [error, setError] = useState(null);

  const readCache = () => {
    try {
      const raw = sessionStorage.getItem(CACHE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (Date.now() - parsed.timestamp > CACHE_TTL) return null;
      return parsed;
    } catch {
      return null;
    }
  };

  const requestLocation = useCallback(() => {
    if (!navigator.geolocation) {
      setStatus("unsupported");
      setError("Geolocation is not supported by your browser.");
      return;
    }

    const cached = readCache();
    if (cached) {
      setLocation({ lat: cached.lat, lng: cached.lng });
      setStatus("granted");
      return;
    }

    setStatus("locating");
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const coords = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        };
        setLocation(coords);
        setStatus("");
        setError(null);
        try {
          sessionStorage.setItem(
            CACHE_KEY,
            JSON.stringify({ ...coords, timestamp: Date.now() })
          );
        } catch {
          // sessionStorage unavailable — not critical, just skip caching
        }
      },
      (err) => {
        setStatus(err.code === err.PERMISSION_DENIED ? "denied" : "error");
        setError(err.message);
      },
      { enableHighAccuracy: false, timeout: 10000, maximumAge: CACHE_TTL }
    );
  }, []);

  useEffect(() => {
    requestLocation();
  }, [requestLocation]);

  return { location, status, error, requestLocation };
}